/**
 * FeeVsSalaryCard — Fee vs post-grad salary payback card.
 *
 * Renders when a degree has BOTH:
 *   - A representative post-grad median salary in USD (`salary_usd`)
 *   - An international tuition fee in USD (`tuition_intl_usd`)
 *
 * UK degrees pass HESA LEO3 (3-year post-grad). US degrees pass
 * Scorecard `median_earnings_10yr_usd` (10-year post-entry). The card
 * auto-labels its own salary metric via the `salary_label` prop.
 *
 * Feb 2026 redesign: the punchline is "years-to-payback", not the
 * underlying tuition/salary inputs. Payback numbers are now the
 * primary display; tuition and salary are collapsed into a single
 * one-line footnote below. Card also relocated from Cluster 3 (Cost)
 * to Cluster 4 (Return on investment) where it semantically belongs.
 */
import { useCurrency } from "@/context/CurrencyContext";
import { TrendingUp } from "lucide-react";
import ChartVerdict from "./ChartVerdict";

// Payback bands (in years) — tuned so the "good / okay / long" split
// is calibrated to student intuition: a US private undergrad at ~$300k
// tuition and a $60k grad salary sits at ~7 yrs, which reads as
// long. UK Home at £10,050 × 3 vs a £26k salary sits at ~1.5 yrs
// which reads as fast.
function paybackTone(years) {
  if (years == null) return { tone: "neutral", label: "—" };
  if (years <= 2) return { tone: "fast",    label: "Fast payback" };
  if (years <= 4) return { tone: "okay",    label: "Reasonable" };
  return { tone: "long", label: "Long payback" };
}

const TONE_CLASSES = {
  fast:    "text-emerald-700",
  okay:    "text-amber-700",
  long:    "text-orange-700",
  neutral: "text-stone-500",
};

export default function FeeVsSalaryCard({
  salary_usd,
  salary_label = "Median salary",
  salary_source = "HESA LEO 3-year median",
  tuition_intl_usd,
  tuition_home_usd,
  duration_years,
  home_label = "Home",
  intl_label = "International",
  take_home_pct = 0.72,
}) {
  const { format } = useCurrency();
  if (!salary_usd || salary_usd <= 0) return null;
  if (!tuition_intl_usd || tuition_intl_usd <= 0) return null;

  const yr = duration_years || 3;
  const totalIntl = tuition_intl_usd * yr;
  const totalHome = tuition_home_usd != null ? tuition_home_usd * yr : null;
  const takeHomeAnnual = Math.round(salary_usd * take_home_pct);
  const paybackIntl = takeHomeAnnual > 0 ? (totalIntl / takeHomeAnnual) : null;
  const paybackHome = totalHome != null && takeHomeAnnual > 0
    ? (totalHome / takeHomeAnnual)
    : null;
  const fmtYears = (y) => (y == null ? "—" : y < 5 ? `${y.toFixed(1)}` : `${Math.round(y)}`);

  return (
    <div
      data-testid="fee-vs-salary-card"
      className="mt-6 mb-8 rounded-lg border border-stone-200 bg-white p-6 lg:p-7"
    >
      <div className="flex items-start gap-2 mb-1">
        <TrendingUp className="h-4 w-4 text-[#4C1D95] mt-0.5" aria-hidden />
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4C1D95]">
          Fee vs salary payback
        </div>
      </div>
      <p className="text-xs text-stone-500 max-w-3xl">
        Years of typical post-tax salary needed to cover full tuition —
        the fastest way to gut-check whether the sticker price of this
        course makes financial sense.
      </p>

      {/* Headline: big payback numbers, side by side */}
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {paybackHome != null && (
          <PaybackHeadline
            variant={home_label}
            years={paybackHome}
            testid="fvs-payback-home"
          />
        )}
        <PaybackHeadline
          variant={intl_label}
          years={paybackIntl}
          testid="fvs-payback-intl"
        />
      </div>

      {/* Footnote: the underlying inputs, small and muted so the
          payback figure keeps the eye. */}
      <div
        className="mt-5 pt-4 border-t border-stone-100 text-[11px] text-stone-500 leading-relaxed font-mono"
        data-testid="fvs-footnote"
      >
        Based on {yr}-yr tuition
        {totalHome != null && <> · {home_label} {format(totalHome)}</>}
        {" "}· {intl_label} {format(totalIntl)}
        {" "}· {salary_label} {format(salary_usd)} <span className="opacity-70">({salary_source})</span>
        {" "}· ~{Math.round(take_home_pct * 100)}% take-home. Living costs and
        scholarships not included.
      </div>

      {/* One-line verdict — reads the primary payback figure and tags
          it fast / reasonable / long so the family gets a takeaway
          before drilling into the underlying numbers. */}
      {(() => {
        const primary = paybackIntl;
        if (primary == null) return null;
        const { tone: bandKey, label } = paybackTone(primary);
        const tone = bandKey === "fast" ? "positive" : bandKey === "okay" ? "neutral" : "warning";
        const yrs = primary < 5 ? primary.toFixed(1) : Math.round(primary);
        const homeCopy = paybackHome != null && paybackHome !== primary
          ? ` (${home_label}: ${paybackHome < 5 ? paybackHome.toFixed(1) : Math.round(paybackHome)} yrs)`
          : "";
        return (
          <ChartVerdict
            testId="verdict-fee-vs-salary"
            tone={tone}
            text={`${intl_label} tuition pays back in ~${yrs} years of take-home pay — the "${label}" band${homeCopy}.`}
          />
        );
      })()}
    </div>
  );

  function PaybackHeadline({ variant, years, testid }) {
    const { tone, label } = paybackTone(years);
    return (
      <div className="rounded-md bg-stone-50 border border-stone-100 px-5 py-4">
        <div className="text-[10px] font-underground-sc tracking-[0.18em] text-stone-500">
          {variant} · payback
        </div>
        <div className="mt-1 flex items-baseline gap-2">
          <span
            className={`font-editorial text-4xl sm:text-5xl leading-none ${TONE_CLASSES[tone]}`}
            data-testid={testid}
          >
            {fmtYears(years)}
          </span>
          <span className={`text-sm font-mono ${TONE_CLASSES[tone]}`}>yrs</span>
        </div>
        <div className={`mt-1 text-[11px] font-underground-sc tracking-wider ${TONE_CLASSES[tone]}`}>
          {label}
        </div>
      </div>
    );
  }
}
